import { useId, useState } from 'react';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';

import type { UiBudgetLane, UiBudgetPoint } from '@nightshift/ui-types';

import { absoluteTime, relativeShort } from '../lib/format.ts';
import { formatAxisValue, formatMetricValue, formatTargetLine, latestPoint, shortSha } from '../lib/budgets.ts';
import { type LanePick } from '../lib/budget-lanes.ts';
import { axisDateLabel, performanceSeries, type SeriesPoint } from '../lib/series.ts';
import { assignSeriesColours } from '../lib/chart-palette.ts';
import { ChartGrid, ChartTimeAxis, ChartValueAxis } from './chart/axes.tsx';
import { plotBox, useContainerWidth } from './chart/plot.tsx';
import './chart/chart.css';
import './budget-lane.css';

const LANE_HEIGHT = 260;
const PAD_TOP = 24;
const PAD_BOTTOM = 30;
const PAD_LEFT = 64;

function machineLines(points: SeriesPoint[]): SeriesPoint[][] {
  const groups = new Map<string, SeriesPoint[]>();
  for (const point of points) {
    const group = groups.get(point.machine) ?? [];
    group.push(point);
    groups.set(point.machine, group);
  }
  return [...groups.values()];
}

export function hiddenProbesAfterToggle(
  probes: readonly string[],
  hidden: readonly string[],
  openProbe: string | null,
  clicked: string,
): string[] {
  if (openProbe) {
    return clicked === openProbe
      ? []
      : probes.filter((name) => name !== openProbe && name !== clicked);
  }
  return hidden.includes(clicked)
    ? hidden.filter((name) => name !== clicked)
    : [...hidden, clicked];
}

export function BudgetLanePanel({
  lane,
  hue,
  windowFrom,
  windowTo,
  now,
  probe,
  picked,
  onPick,
  onSelectProbe,
}: {
  lane: UiBudgetLane;
  hue: string;
  windowFrom: number;
  windowTo: number;
  now: number;
  probe: string | null;
  picked: LanePick | null;
  onPick: (pick: LanePick) => void;
  onSelectProbe: (probe: string | null) => void;
}) {
  const [hidden, setHidden] = useState<string[]>([]);
  const [focused, setFocused] = useState(true);
  const openProbe = probe && lane.probes.some((entry) => entry.name === probe) ? probe : null;
  const visible = lane.probes.filter((entry) => openProbe ? entry.name === openProbe : !hidden.includes(entry.name));
  const visibleLane = { ...lane, probes: visible, nightly: visible.flatMap((entry) => entry.nightly), mr: visible.flatMap((entry) => entry.mr) };
  const latest = latestPoint(visibleLane);
  const series = performanceSeries(visibleLane, { from: windowFrom, to: windowTo }, null, focused);
  const count = visibleLane.nightly.length + visibleLane.mr.length;
  // Assigned over every probe the lane has, not the visible subset: hiding one must
  // not repaint the others.
  const probeColours = assignSeriesColours(lane.probes.map((entry) => entry.name));
  const probeColour = (name: string): string => probeColours.get(name) ?? 'var(--text-4)';

  return (
    <section
      className={`lane${lane.regression ? ' is-regression' : ''}`}
      id={`budget-lane-${lane.id}`}
      aria-label={lane.label}
    >
      <header className="lane-head">
        <span className="lane-swatch" style={{ background: hue }} aria-hidden="true" />
        <h3 className="lane-title">{lane.label}</h3>
        <span className="lane-metric mono">{lane.metric}</span>
        {lane.regression ? (
          <span className="lane-lamp is-lit" title="A probe is off target or worse than nightly.">
            <span className="lane-lamp-face" />
            regression
          </span>
        ) : lane.status === 'no-baseline' ? (
          <span className="lane-lamp" title="MR samples have no nightly to compare.">
            <span className="lane-lamp-face" />
            no baseline
          </span>
        ) : lane.status === 'empty' ? (
          <span className="lane-lamp" title="No samples in this window.">
            <span className="lane-lamp-face" />
            no samples
          </span>
        ) : null}
        <span className="lane-figures">
          <span className="lane-latest">{latest ? formatMetricValue(lane.unit, latest.value) : '—'}</span>
          <span className="lane-target-word">{formatTargetLine(lane)}</span>
        </span>
        <span className="lane-window">
          {count} samples · {dayLabel(windowFrom)} → {dayLabel(windowTo)}
          {latest ? ` · ${relativeShort(latest.t, now)} ago` : ''}
        </span>
      </header>

      {lane.probes.length > 0 ? (
        <div className="lane-probes" role="group" aria-label="Probes">
          <button
            type="button"
            className="lane-probe"
            aria-pressed={openProbe === null && hidden.length === 0}
            onClick={() => { setHidden([]); onSelectProbe(null); }}
          >
            All probes
          </button>
          {lane.probes.map((entry) => (
            <button
              key={entry.name}
              type="button"
              className="lane-probe"
              aria-pressed={visible.some((item) => item.name === entry.name)}
              onClick={() => {
                setHidden(
                  hiddenProbesAfterToggle(
                    lane.probes.map((item) => item.name),
                    hidden,
                    openProbe,
                    entry.name,
                  ),
                );
                onSelectProbe(null);
              }}
            >
              <span className="lane-swatch" style={{ background: probeColour(entry.name) }} />
              {entry.regression ? <span className="lane-probe-lamp" aria-hidden="true" /> : null}
              {entry.name || 'Default probe'}
            </button>
          ))}
        </div>
      ) : null}

      <div className="lane-summary">
        <span>{count} samples · {visible.length} {visible.length === 1 ? 'probe' : 'probes'} · latest {latest ? formatMetricValue(lane.unit, latest.value) : '—'}</span>
        <button className="btn btn-sm" type="button" aria-pressed={focused} onClick={() => setFocused(!focused)}>
          {focused ? 'Scale: typical range' : 'Scale: all values'}
        </button>
        {series.outlierCount > 0 ? <span className="lane-outlier-note">{series.outlierCount} outlier{series.outlierCount === 1 ? '' : 's'}{focused ? ' · pinned to chart edge if outside scale' : ''}</span> : null}
      </div>
      {count === 0 ? <p className="lane-empty">{lane.probes.length ? 'Select a probe above to compare its measurements.' : 'No measurements in this range. Widen the dates or choose Auto.'}</p> : null}

      <ValueLane
        unit={lane.unit}
        series={series}
        laneId={lane.id}
        probeColour={probeColour}
        picked={picked?.laneId === lane.id ? picked : null}
        onPick={onPick}
      />
    </section>
  );
}

function ValueLane({
  unit,
  series,
  laneId,
  probeColour,
  picked,
  onPick,
}: {
  unit: string;
  series: ReturnType<typeof performanceSeries>;
  laneId: string;
  probeColour: (name: string) => string;
  picked: LanePick | null;
  onPick: (pick: LanePick) => void;
}) {
  const clipId = useId();
  const [hover, setHover] = useState<UiBudgetPoint | null>(null);
  const { ref, width } = useContainerWidth();
  const padRight = series.target === null ? 18 : 88;
  const box = plotBox(width, LANE_HEIGHT, {
    top: PAD_TOP,
    right: padRight,
    bottom: PAD_BOTTOM,
    left: PAD_LEFT,
  });
  const plotW = box.innerWidth;
  const plotH = box.innerHeight;

  // Scales over the raw data, so the lane no longer carries its own normalisation.
  // Positions are plot-relative; the marks sit inside a translated group and the
  // HTML overlays add the padding back.
  const xScale = scaleLinear<number>({ domain: series.window, range: [0, plotW] });
  const yScale = scaleLinear<number>({ domain: series.domain, range: [plotH, 0] });
  // Outliers pin to the edge under "typical range" rather than escaping the plot.
  const clampY = (value: number): number => Math.max(0, Math.min(plotH, yScale(value)));
  const px = (at: number): number => PAD_LEFT + xScale(at);
  const py = (value: number): number => PAD_TOP + clampY(value);

  const targetY = series.target === null ? null : clampY(series.target);

  const hoverPoint = hover;

  return (
    <div className="lane-plot" ref={ref} onMouseLeave={() => setHover(null)}>
      {width > 0 ? (
        <>
          <svg
            className="lane-svg"
            width={width}
            height={LANE_HEIGHT}
            viewBox={`0 0 ${width} ${LANE_HEIGHT}`}
            aria-hidden="true"
          >
            <defs>
              {/* Referenced from inside the translated group, so it is plot-relative. */}
              <clipPath id={clipId}>
                <rect x={0} y={0} width={plotW} height={plotH} />
              </clipPath>
            </defs>

            <Group left={PAD_LEFT} top={PAD_TOP}>
              {targetY !== null ? (
                <rect
                  className="lane-danger-band"
                  clipPath={`url(#${clipId})`}
                  x={0}
                  y={series.ceiling ? 0 : targetY}
                  width={plotW}
                  height={series.ceiling ? Math.max(0, targetY) : Math.max(0, plotH - targetY)}
                />
              ) : null}

              <ChartGrid box={box} scale={yScale} />
              <ChartValueAxis
                scale={yScale}
                format={(value) => formatAxisValue(unit, value, series.step)}
              />

              {series.probes.map((probe) => (['nightly', 'mr'] as const).map((context) =>
                machineLines(probe[context]).map((points, index) => (
                  <LinePath<SeriesPoint>
                    key={`${probe.name}-${context}-${index}`}
                    className="lane-line"
                    clipPath={`url(#${clipId})`}
                    data={points}
                    x={(point) => xScale(point.t)}
                    y={(point) => clampY(point.value)}
                    stroke={probeColour(probe.name)}
                    strokeDasharray={context === 'mr' ? '3 5' : undefined}
                  />
                )),
              ))}

              {targetY !== null && series.target !== null ? (
                <>
                  <line
                    className="lane-target-line"
                    x1={0}
                    x2={plotW}
                    y1={targetY}
                    y2={targetY}
                  />
                  <text className="lane-target-label" x={plotW + 8} y={targetY + 3.5}>
                    {formatMetricValue(unit, series.target, series.step)}
                  </text>
                </>
              ) : null}

              <ChartTimeAxis box={box} scale={xScale} format={axisDateLabel} />
            </Group>
          </svg>

          {series.probes.map((probe) =>
            probe.nightly.map((pt, index) => (
              <button
                key={`n-${probe.name}-${pt.t}-${index}`}
                type="button"
                className={`lane-point is-nightly${pt.outlier ? ' is-outlier' : ''}${pt.regression ? ' is-regression' : ''}${
                  picked?.context === 'nightly' && picked.probe === probe.name && picked.index === index
                    ? ' is-picked'
                    : ''
                }`}
                style={{ left: px(pt.t), top: py(pt.value), color: probeColour(probe.name) }}
                title={`${probe.name || 'Default probe'} · ${formatMetricValue(unit, pt.value)}${pt.outlier ? ' · statistical outlier (3× IQR)' : ''}`}
                aria-label={`${probe.name} ${pt.outlier ? 'Outlier ' : ''}Nightly ${formatMetricValue(unit, pt.value)} · ${pt.machine} · ${shortSha(pt.sha)} · ${absoluteTime(pt.t)}`}
                aria-pressed={
                  picked?.context === 'nightly' && picked.probe === probe.name && picked.index === index
                }
                onMouseEnter={() => setHover(toPoint(pt))}
                onFocus={() => setHover(toPoint(pt))}
                onBlur={() => setHover(null)}
                onClick={() => onPick({ laneId, context: 'nightly', index, probe: probe.name })}
              />
            )),
          )}

          {series.probes.map((probe) =>
            probe.mr.map((pt, index) => (
              <button
                key={`m-${probe.name}-${pt.t}-${index}`}
                type="button"
                className={`lane-point is-mr${pt.outlier ? ' is-outlier' : ''}${pt.regression ? ' is-regression' : ''}${pt.noBaseline ? ' is-no-baseline' : ''}${
                  picked?.context === 'mr' && picked.probe === probe.name && picked.index === index
                    ? ' is-picked'
                    : ''
                }`}
                style={{ left: px(pt.t), top: py(pt.value), color: probeColour(probe.name) }}
                title={`${probe.name || 'Default probe'} · ${formatMetricValue(unit, pt.value)}${pt.outlier ? ' · statistical outlier (3× IQR)' : ''}`}
                aria-label={`${probe.name} ${pt.outlier ? 'Outlier ' : ''}MR ${formatMetricValue(unit, pt.value)} · ${pt.machine} · ${shortSha(pt.sha)} · ${absoluteTime(pt.t)}`}
                aria-pressed={picked?.context === 'mr' && picked.probe === probe.name && picked.index === index}
                onMouseEnter={() => setHover(toPoint(pt))}
                onFocus={() => setHover(toPoint(pt))}
                onBlur={() => setHover(null)}
                onClick={() => onPick({ laneId, context: 'mr', index, probe: probe.name })}
              />
            )),
          )}

          {hoverPoint ? (
            <LaneTooltip
              x={clampTooltipX(px(hoverPoint.t), width)}
              y={Math.max(80, py(hoverPoint.value))}
              line1={`${formatMetricValue(unit, hoverPoint.value)} · ${hoverPoint.context}${
                hoverPoint.regression ? ' · regression' : hoverPoint.noBaseline ? ' · no baseline' : ''
              }`}
              line2={`${hoverPoint.probe ?? 'run'} · ${hoverPoint.machine} · ${shortSha(hoverPoint.sha)} · ${absoluteTime(hoverPoint.t)}`}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function toPoint(pt: {
  t: number;
  value: number;
  regression: boolean;
  noBaseline: boolean;
  machine: string;
  sha: string;
  probe: string | null;
  context: 'nightly' | 'mr';
}): UiBudgetPoint {
  return {
    t: pt.t,
    value: pt.value,
    machine: pt.machine,
    sha: pt.sha,
    probe: pt.probe,
    context: pt.context,
    pipelineId: null,
    regression: pt.regression,
    noBaseline: pt.noBaseline,
  };
}

function LaneTooltip({ x, y, line1, line2 }: { x: number; y: number; line1: string; line2: string }) {
  return (
    <div className="lane-tooltip" style={{ left: x, top: y }} role="presentation">
      <span className="lane-tooltip-value">{line1}</span>
      <span className="lane-tooltip-meta">{line2}</span>
    </div>
  );
}

function clampTooltipX(x: number, width: number): number {
  return Math.min(width - 110, Math.max(110, x));
}

function dayLabel(at: number): string {
  return new Date(at).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
